
import React from 'react';
import { PAYMENT_CONFIG, formatCurrency, formatViews } from '../lib/paymentConfig';

const ApplicationTerms: React.FC = () => {
    return (
        <div className="max-w-3xl mx-auto px-4 py-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl font-black text-gray-900 mb-2">Creator Terms & Conditions</h1>
            <p className="text-sm text-gray-400 font-medium mb-8">Last Updated: February 24, 2026</p>

            <div className="bg-white rounded-[2rem] p-8 md:p-12 border border-gray-100 shadow-xl space-y-8 text-gray-700 leading-relaxed font-medium">

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">1. Agreement to Terms</h2>
                    <p>
                        By applying to or participating in the Queenli Creator Program ("Program"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do not apply or participate.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">2. Program Overview</h2>
                    <p>
                        Queenli engages content creators to produce User Generated Content (UGC) for marketing and distribution on official Queenli social media accounts. By applying, you agree to provide original content created by you. No posting to your own profile is required.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">3. Eligibility</h2>
                    <p>
                        You must be at least 18 years old to participate in the Program. By applying, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">4. Deliverables & Payment</h2>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Deliverables:</strong> {PAYMENT_CONFIG.videosPerMonth} edited videos per month + raw footage (1 video per weekday).</li>
                        <li><strong>Base Pay:</strong> {formatCurrency(PAYMENT_CONFIG.perVideoRate)} USD per approved video ({formatCurrency(PAYMENT_CONFIG.monthlyBase)}/month at {PAYMENT_CONFIG.videosPerMonth} videos).</li>
                        <li><strong>Performance Bonus:</strong> {formatCurrency(PAYMENT_CONFIG.cpmRate)} per 1,000 views for videos exceeding {formatViews(PAYMENT_CONFIG.viewThreshold)} views on official brand accounts.</li>
                        <li><strong>Bonus Cap:</strong> Maximum bonus per video is {formatCurrency(PAYMENT_CONFIG.maxBonusPerVideo)} USD.</li>
                        <li><strong>Payment Schedule:</strong> Payments are made on the first Monday of each month for the previous month's work.</li>
                    </ul>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">5. Content & Usage Rights</h2>
                    <p>
                        You grant Queenli a non-exclusive, perpetual, worldwide, royalty-free license to use, reproduce, modify, distribute, and display your submitted content for advertising, marketing, and distribution purposes across all platforms. You agree not to post this content on your own channels without written permission.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">6. Originality & Intellectual Property</h2>
                    <p>
                        You represent and warrant that all content submitted is your original work and does not infringe on the intellectual property rights of any third party. You retain ownership of your original content, subject to the license granted in Section 5.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">7. Creator Conduct</h2>
                    <p>You agree NOT to:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>Submit content that is not your original work</li>
                        <li>Share your account credentials with others</li>
                        <li>Attempt to gain unauthorized access to the Platform or its systems</li>
                        <li>Upload malicious content or code</li>
                        <li>Misrepresent your identity or qualifications</li>
                        <li>Violate any applicable laws or regulations</li>
                    </ul>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">8. Disclaimers</h2>
                    <p>
                        The Platform is provided "AS IS" and "AS AVAILABLE" without warranties of any kind. We do not guarantee any specific level of views, engagement, or bonus earnings on your content.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">9. Limitation of Liability</h2>
                    <p>To the maximum extent permitted by law, Queenli and its officers, directors, employees, and agents shall not be liable for:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                        <li>Loss of profits, data, or goodwill</li>
                        <li>Service interruptions</li>
                        <li>Any damages exceeding the amount paid to you in the twelve (12) months preceding the claim</li>
                    </ul>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">10. Termination</h2>

                    <h3 className="text-lg font-bold text-gray-800">By You:</h3>
                    <p>You may leave the Program at any time by notifying us. Outstanding payments for completed work will still be processed.</p>

                    <h3 className="text-lg font-bold text-gray-800">By Us:</h3>
                    <p>Queenli reserves the right to terminate your participation in the Program at any time if content quality drops, deliverables are consistently missed, or these Terms are violated.</p>

                    <h3 className="text-lg font-bold text-gray-800">Effect of Termination:</h3>
                    <p>Upon termination, your access to the creator dashboard will be revoked. The content license granted in Section 5 survives termination.</p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">11. Changes to Terms</h2>
                    <p>
                        We reserve the right to modify these Terms at any time. We will notify you of material changes by posting updated Terms on the Platform and updating the "Last Updated" date. Continued participation in the Program after changes constitutes acceptance of the new Terms.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">12. Governing Law & Disputes</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of your jurisdiction, without regard to its conflict of law provisions. Any disputes shall first be resolved informally by contacting us. If informal resolution fails, disputes shall be resolved through binding arbitration.
                    </p>
                </section>

                <section className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-900">13. Contact Information</h2>
                    <p>For questions about these Terms, please contact us at:</p>
                    <p>
                        Email: <a href="mailto:contact@torek.io" className="text-purple-600 font-bold hover:underline">contact@torek.io</a>
                    </p>
                </section>

                <div className="pt-8 border-t border-gray-100 text-center space-y-4">
                    <p className="text-sm text-gray-500">
                        By participating in the Queenli Creator Program, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions and our <a href="/privacy" className="text-purple-600 font-bold hover:underline">Privacy Policy</a>.
                    </p>
                    <p className="text-xs text-gray-400">
                        Queenli is a product of Torek, LLC.
                    </p>
                </div>

            </div>
        </div>
    );
};

export default ApplicationTerms;
